import {
  useCallback,
  forwardRef,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AnimatePresence,
  LayoutGroup,
  motion,
  useReducedMotion,
} from 'framer-motion';
import { CrossIcon } from '../../../assets/icons/icons';
import { HISTORY_PANEL_TRANSITION } from './historyMotion';
import './HistorySummaryFilter.css';

const SUMMARY_TYPE = Object.freeze({
  ALL: 'all',
  EXPENSE: 'expense',
  INCOME: 'income',
});

const DEFAULT_LABELS = Object.freeze({
  ariaLabel: 'Transaction summary',
  expense: 'Spending',
  income: 'Income',
  selectExpenseAria: 'Show spending transactions',
  selectIncomeAria: 'Show income transactions',
  clearAria: 'Show all transactions',
  loadingAmountAria: 'Loading totals',
});

const SUMMARY_SURFACE_TRANSITION = Object.freeze({
  layout: HISTORY_PANEL_TRANSITION,
});
const SUMMARY_CONTENT_TRANSITION = Object.freeze({
  duration: 0.18,
  delay: 0.16,
  ease: [0.22, 1, 0.36, 1],
});
const SUMMARY_CONTENT_EXIT_TRANSITION = Object.freeze({
  duration: 0.12,
  ease: [0.32, 0, 0.2, 1],
});
const SUMMARY_SHARED_EXIT_TRANSITION = Object.freeze({
  ...HISTORY_PANEL_TRANSITION,
});
const SUMMARY_UNMATCHED_EXIT_TRANSITION = Object.freeze({
  duration: 0.18,
  ease: [0.32, 0, 0.2, 1],
});
const SUMMARY_REDUCED_TRANSITION = Object.freeze({ duration: 0 });
const SUMMARY_MOTION_STYLE = Object.freeze({
  '--history-summary-motion-duration': `${HISTORY_PANEL_TRANSITION.duration}s`,
  '--history-summary-motion-ease': `cubic-bezier(${HISTORY_PANEL_TRANSITION.ease.join(', ')})`,
});
const SummarySurface = memo(function SummarySurface({
  expanded,
  onReturnComplete,
  returning,
  shouldReduceMotion,
  type,
}) {
  useEffect(() => {
    if (!expanded && returning && shouldReduceMotion) {
      onReturnComplete?.(type);
    }
  }, [expanded, onReturnComplete, returning, shouldReduceMotion, type]);

  const handleTransitionEnd = (event) => {
    if (
      event.target === event.currentTarget
      && event.propertyName === 'width'
      && !expanded
      && returning
    ) {
      onReturnComplete?.(type);
    }
  };

  return (
    <span
      className={[
        'history-summary-filter__surface',
        `history-summary-filter__surface--${type}`,
        expanded ? 'history-summary-filter__surface--expanded' : '',
        expanded || returning ? 'history-summary-filter__surface--elevated' : '',
      ].filter(Boolean).join(' ')}
      onTransitionEnd={handleTransitionEnd}
      aria-hidden="true"
    />
  );
});

function assignRef(ref, value) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}

function SummaryAmount({ amount, initialLoading, selected = false }) {
  const className = [
    'history-summary-filter__amount',
    selected ? 'history-summary-filter__amount--selected' : '',
    initialLoading ? 'history-summary-filter__amount--loading' : '',
  ].filter(Boolean).join(' ');

  return (
    <span
      className={className}
      data-testid={initialLoading ? 'history-total-skeleton' : undefined}
      aria-hidden={initialLoading ? 'true' : undefined}
    >
      {initialLoading ? null : amount}
    </span>
  );
}

const SummaryPersistentCopy = memo(function SummaryPersistentCopy({
  amount,
  behind,
  initialLoading,
  label,
  selected,
  surfaceTransition,
  type,
}) {
  return (
    <motion.div
      className={[
        'history-summary-filter__copy-anchor',
        `history-summary-filter__copy-anchor--${type}`,
        selected ? 'history-summary-filter__copy-anchor--selected' : '',
        behind ? 'history-summary-filter__copy-anchor--behind' : '',
      ].filter(Boolean).join(' ')}
      layout="position"
      layoutDependency={selected}
      transition={surfaceTransition}
      aria-hidden={selected ? undefined : 'true'}
    >
      <div
        className="history-summary-filter__choice-copy history-summary-filter__persistent-copy"
      >
        <SummaryAmount
          amount={amount}
          initialLoading={initialLoading}
          selected={selected}
        />
        <span className={[
          'history-summary-filter__label',
          selected ? 'history-summary-filter__label--selected' : '',
        ].filter(Boolean).join(' ')}>
          {label}
        </span>
      </div>
    </motion.div>
  );
});

const SummaryChoice = forwardRef(function SummaryChoice({
  amount,
  ariaLabel,
  buttonRef,
  disabled,
  initialLoading,
  label,
  loadingAmountAria,
  onSelect,
  shouldReduceMotion,
  type,
}, ref) {
  const exitVariants = useMemo(() => ({
    exit: (nextSelectedType) => {
      const sharesSurface = nextSelectedType === type;

      return {
        opacity: sharesSurface ? 1 : 0,
        transition: shouldReduceMotion
          ? SUMMARY_REDUCED_TRANSITION
          : (sharesSurface
            ? SUMMARY_SHARED_EXIT_TRANSITION
            : SUMMARY_UNMATCHED_EXIT_TRANSITION),
      };
    },
  }), [shouldReduceMotion, type]);
  return (
    <motion.button
      type="button"
      className={`history-summary-filter__card history-summary-filter__card--${type}`}
      aria-label={initialLoading
        ? `${label}. ${loadingAmountAria}. ${ariaLabel}`
        : `${label}: ${amount}. ${ariaLabel}`}
      aria-pressed={false}
      disabled={disabled}
      ref={(node) => {
        assignRef(ref, node);
        assignRef(buttonRef, node);
      }}
      variants={exitVariants}
      initial={false}
      exit="exit"
      onClick={onSelect}
    >
    </motion.button>
  );
});

const SummarySelected = forwardRef(function SummarySelected({
  clearButtonRef,
  clearAriaLabel,
  contentExitTransition,
  contentTransition,
  onClear,
  shouldReduceMotion,
  type,
}, ref) {
  const exitVariants = useMemo(() => ({
    exit: (nextSelectedType) => {
      const sharesSurface = nextSelectedType === null || nextSelectedType === type;

      return {
        opacity: sharesSurface ? 1 : 0,
        transition: shouldReduceMotion
          ? SUMMARY_REDUCED_TRANSITION
          : (sharesSurface
            ? SUMMARY_SHARED_EXIT_TRANSITION
            : SUMMARY_UNMATCHED_EXIT_TRANSITION),
      };
    },
  }), [shouldReduceMotion, type]);
  return (
    <motion.button
      type="button"
      className={[
        'history-summary-filter__card',
        'history-summary-filter__card--selected',
        `history-summary-filter__card--${type}`,
      ].join(' ')}
      aria-label={clearAriaLabel}
      ref={(node) => {
        assignRef(ref, node);
        assignRef(clearButtonRef, node);
      }}
      variants={exitVariants}
      initial={false}
      exit="exit"
      onClick={onClear}
    >
      <motion.span
        className="history-summary-filter__clear"
        aria-hidden="true"
        initial={shouldReduceMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={shouldReduceMotion
          ? undefined
          : { opacity: 0, transition: contentExitTransition }}
        transition={contentTransition}
      >
        <CrossIcon className="history-summary-filter__clear-icon" />
      </motion.span>
    </motion.button>
  );
});

/**
 * Presentation-only selector for the operation direction. Amounts are
 * preformatted by the caller.
 */
function HistorySummaryFilter({
  activeType = SUMMARY_TYPE.ALL,
  expenseAmount = '—',
  incomeAmount = '—',
  initialLoading = false,
  labels = DEFAULT_LABELS,
  loading = false,
  onChange,
}) {
  const shouldReduceMotion = useReducedMotion();
  const expenseChoiceRef = useRef(null);
  const incomeChoiceRef = useRef(null);
  const clearButtonRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const lastSelectedTypeRef = useRef(null);
  const [returningType, setReturningType] = useState(null);
  const copy = { ...DEFAULT_LABELS, ...labels };
  const isExpenseSelected = activeType === SUMMARY_TYPE.EXPENSE;
  const isIncomeSelected = activeType === SUMMARY_TYPE.INCOME;
  const selectedType = isExpenseSelected
    ? SUMMARY_TYPE.EXPENSE
    : (isIncomeSelected ? SUMMARY_TYPE.INCOME : null);
  if (selectedType) {
    lastSelectedTypeRef.current = selectedType;
  }
  const effectiveReturningType = selectedType
    ? null
    : (returningType || lastSelectedTypeRef.current);
  const surfaceTransition = shouldReduceMotion
    ? SUMMARY_REDUCED_TRANSITION
    : SUMMARY_SURFACE_TRANSITION;
  const contentTransition = shouldReduceMotion
    ? SUMMARY_REDUCED_TRANSITION
    : SUMMARY_CONTENT_TRANSITION;
  const contentExitTransition = shouldReduceMotion
    ? SUMMARY_REDUCED_TRANSITION
    : SUMMARY_CONTENT_EXIT_TRANSITION;

  useEffect(() => {
    if (selectedType) {
      setReturningType(null);
      return;
    }

    if (lastSelectedTypeRef.current) {
      setReturningType(lastSelectedTypeRef.current);
    }
  }, [selectedType]);

  const handleSurfaceReturnComplete = useCallback((type) => {
    if (lastSelectedTypeRef.current === type) {
      lastSelectedTypeRef.current = null;
    }

    setReturningType((current) => (current === type ? null : current));
  }, []);

  useEffect(() => {
    const pendingFocus = pendingFocusRef.current;

    if (!pendingFocus) {
      return;
    }

    const target = pendingFocus === 'clear'
      ? clearButtonRef.current
      : (pendingFocus === SUMMARY_TYPE.EXPENSE
        ? expenseChoiceRef.current
        : incomeChoiceRef.current);

    if (target) {
      target.focus({ preventScroll: true });
      pendingFocusRef.current = null;
    }
  }, [selectedType]);

  const changeType = (nextType) => {
    if (typeof onChange === 'function') {
      pendingFocusRef.current = nextType === SUMMARY_TYPE.ALL
        ? selectedType
        : 'clear';
      onChange(nextType);
    }
  };

  return (
    <section
      className={[
        'history-summary-filter',
        selectedType
          ? 'history-summary-filter--selected'
          : 'history-summary-filter--all',
        loading ? 'history-summary-filter--loading' : '',
        initialLoading ? 'history-summary-filter--initial-loading' : '',
      ].filter(Boolean).join(' ')}
      role="group"
      aria-label={copy.ariaLabel}
      aria-busy={loading || initialLoading}
      style={SUMMARY_MOTION_STYLE}
    >
      <LayoutGroup id="history-summary-filter">
        <SummarySurface
          expanded={isExpenseSelected}
          onReturnComplete={handleSurfaceReturnComplete}
          returning={effectiveReturningType === SUMMARY_TYPE.EXPENSE}
          shouldReduceMotion={shouldReduceMotion}
          type={SUMMARY_TYPE.EXPENSE}
        />
        <SummarySurface
          expanded={isIncomeSelected}
          onReturnComplete={handleSurfaceReturnComplete}
          returning={effectiveReturningType === SUMMARY_TYPE.INCOME}
          shouldReduceMotion={shouldReduceMotion}
          type={SUMMARY_TYPE.INCOME}
        />

        <SummaryPersistentCopy
          amount={expenseAmount}
          behind={isIncomeSelected
            || effectiveReturningType === SUMMARY_TYPE.INCOME}
          initialLoading={initialLoading}
          label={copy.expense}
          selected={isExpenseSelected}
          surfaceTransition={surfaceTransition}
          type={SUMMARY_TYPE.EXPENSE}
        />
        <SummaryPersistentCopy
          amount={incomeAmount}
          behind={isExpenseSelected
            || effectiveReturningType === SUMMARY_TYPE.EXPENSE}
          initialLoading={initialLoading}
          label={copy.income}
          selected={isIncomeSelected}
          surfaceTransition={surfaceTransition}
          type={SUMMARY_TYPE.INCOME}
        />

        <div
          className="history-summary-filter__base"
          aria-hidden={selectedType ? 'true' : undefined}
        >
          <div className="history-summary-filter__slot">
            <AnimatePresence custom={selectedType} initial={false}>
              {!isExpenseSelected ? (
                <SummaryChoice
                  key="choice-expense"
                  amount={expenseAmount}
                  ariaLabel={copy.selectExpenseAria}
                  buttonRef={expenseChoiceRef}
                  disabled={Boolean(selectedType)}
                  initialLoading={initialLoading}
                  label={copy.expense}
                  loadingAmountAria={copy.loadingAmountAria}
                  onSelect={() => changeType(SUMMARY_TYPE.EXPENSE)}
                  shouldReduceMotion={shouldReduceMotion}
                  type={SUMMARY_TYPE.EXPENSE}
                />
              ) : null}
            </AnimatePresence>
          </div>
          <div className="history-summary-filter__slot">
            <AnimatePresence custom={selectedType} initial={false}>
              {!isIncomeSelected ? (
                <SummaryChoice
                  key="choice-income"
                  amount={incomeAmount}
                  ariaLabel={copy.selectIncomeAria}
                  buttonRef={incomeChoiceRef}
                  disabled={Boolean(selectedType)}
                  initialLoading={initialLoading}
                  label={copy.income}
                  loadingAmountAria={copy.loadingAmountAria}
                  onSelect={() => changeType(SUMMARY_TYPE.INCOME)}
                  shouldReduceMotion={shouldReduceMotion}
                  type={SUMMARY_TYPE.INCOME}
                />
              ) : null}
            </AnimatePresence>
          </div>
        </div>

        <AnimatePresence custom={selectedType} initial={false}>
          {selectedType ? (
            <SummarySelected
              key={`selected-${selectedType}`}
              clearButtonRef={clearButtonRef}
              clearAriaLabel={copy.clearAria}
              contentExitTransition={contentExitTransition}
              contentTransition={contentTransition}
              onClear={() => changeType(SUMMARY_TYPE.ALL)}
              shouldReduceMotion={shouldReduceMotion}
              type={selectedType}
            />
          ) : null}
        </AnimatePresence>
      </LayoutGroup>
    </section>
  );
}

export default HistorySummaryFilter;
